import { FormApi } from 'final-form'
import { cloneDeep, debounce, isEqual, pick } from 'lodash'
import { IntegrationJsonType, StudioSystemType } from 'types/global'
import { MountingID, NativeMountingID, PartnerAccessoriesID } from 'types/mounting'
import { isVanDerValkSuitabilityReadyForBom } from '../vandervalk/getVanDerValkSuitabilityPayload'
import { refreshVanDerValkModuleGridFacetAttachments } from '../vandervalk/refreshVanDerValkModuleGridFacetAttachments'
import getCompatibility from './compatibility'
import { possibleInputVariables } from './constants'
import { saveInputs } from './Inputs'
import { getStructuralOptionsMCS } from './mcs'
import { getMcsRoofFacetData } from './mcsRoofFacets'
import { fastRackOnSync } from './mounting_systems/FastRack/FastRackHooks'
import { novaOnSync } from './mounting_systems/Nova/NovaHooks'
import { snapnrackUltraRailAfterCalculate, clearSnapnrackUltraRailPluginErrors } from './mounting_systems/SnapNRack/SnapNRackHooks'
import { solarPortAfterCalculate, solarPortOnSync } from './mounting_systems/SolarPort/SolarPortHooks'
import getNativeMountingSystems from './mountingSystems'
import { partnerAccessories } from './partnerAccessories'
import { getPanelOrientationsInSystem } from './railing'
import {
  BomGeneratorContext,
  MountingCalcInput,
  MountingCalcResult,
  PanelBlockPrecursor,
  RoofTypeName,
  StructuralOptions,
} from './types'
import { addItemsToSystem, saveRoofFacetData } from './utils'

const mountingCalcGenerationBySystem = new Map<string, number>()

function bumpMountingCalcGeneration(systemUuid: string): number {
  const next = (mountingCalcGenerationBySystem.get(systemUuid) ?? 0) + 1
  mountingCalcGenerationBySystem.set(systemUuid, next)
  return next
}

function isMountingCalcGenerationCurrent(systemUuid: string, generation: number): boolean {
  return mountingCalcGenerationBySystem.get(systemUuid) === generation
}

function isRackingManagedOther(other: { authored_by?: string }): boolean {
  return typeof other.authored_by === 'string' && other.authored_by.startsWith('racking:')
}

/** Passed on pending sync rows queued from OsFacet studio signals (draw / delete / edit). */
export const VDV_FACET_SURFACE_SYNC_ATTRIBUTE = 'facetSurface'

function initMountingData(system: StudioSystemType, mountingName: MountingID) {
  if (!system.integration_json) {
    system.integration_json = {} as IntegrationJsonType
  }
  if (!system.integration_json[mountingName]) system.integration_json[mountingName] = {}
  if (system?.integration_json?.[mountingName]?.enabled === undefined) {
    if (mountingName && system.integration_json[mountingName] !== undefined) {
      system.integration_json[mountingName] = { ...system.integration_json[mountingName], enabled: true }
    }
  }
}

export async function getStructuralOptions(
  target,
  system: StudioSystemType,
  mountingName: MountingID
): Promise<StructuralOptions> {
  if (system.integration_json && system.integration_json?.[mountingName]?.roofHookCalculationMethod === 'mcs') {
    return new Promise((resolve) => {
      getStructuralOptionsMCS(system, target, resolve)
    })
  } else {
    const structuralOptions: StructuralOptions = {}
    return structuralOptions
  }
}

export function getRoofInputs(system: StudioSystemType): MountingCalcInput[] {
  const projectData = window.projectForm.getState().values

  //added function to get rooftype to avoid assigning undefined to var roofType
  const getRoofType = (): RoofTypeName | null => {
    const roofTypeUrl = projectData.roof_type
    if (roofTypeUrl?.length) {
      let roofId = Number(roofTypeUrl.split('roof_types/')[1].split('/')[0])
      const roof = window.AccountHelper.getRoofTypeById(roofId)?.name
      return roof
    } else return null
  }
  const roofTypeName = getRoofType()
  if (!projectData.custom_data) projectData.custom_data = {}
  const roofFacets = getMcsRoofFacetData(system, projectData.custom_data)
  // Create roofInputs
  const roofInputs: MountingCalcInput[] = []
  if (roofFacets && roofFacets.length) {
    roofFacets.forEach((roofFacet, roofIndex) => {
      let panelBlocks: PanelBlockPrecursor[] = []
      if (roofFacet.moduleGridUuids) {
        roofFacet.moduleGridUuids.forEach((moduleGridUuid) => {
          const moduleGrid = window.editor.objectByUuid(moduleGridUuid)
          if (moduleGrid)
            panelBlocks.push({
              moduleGrid,
              top: 0,
              left: 0,
              panel: moduleGrid.moduleType(),
            })
        })
      }
      roofInputs.push({
        panelBlocks,
        roof: {
          roofTypeName,
          roofIndex,
        },
        options: {},
      })
    })
  }
  return roofInputs
}

function clearExistingComponents(
  system: StudioSystemType,
  mountingName: MountingID | undefined,
  autoCalcEnabled: boolean | undefined,
  selectedPartnerAccessories: string[] | undefined
): void {
  if (mountingName && autoCalcEnabled) {
    clear(system)
  }
  if (selectedPartnerAccessories?.length) {
    clearPartnerAccessories(system)
  }
}

function addMergedItemsToSystem(
  system: StudioSystemType,
  mountingName: NativeMountingID,
  mountingResults: MountingCalcResult[],
  partnerAccessoryResults: { manufacturer: string; result: MountingCalcResult }[],
  inputOptions?: Record<string, unknown>
): void {
  const nativeMountingSystems = getNativeMountingSystems()

  if (mountingResults.length) {
    let mergedItems = mountingResults.flatMap((r) => r.items)
    const postProcess = nativeMountingSystems[mountingName]?.postProcessMergedItems
    if (postProcess && inputOptions) {
      mergedItems = postProcess(mergedItems, inputOptions, system)
    }
    const merged = { items: mergedItems } as MountingCalcResult
    addItemsToSystem(system, nativeMountingSystems[mountingName].manufacturer, merged)
  }

  if (partnerAccessoryResults.length) {
    const byManufacturer = new Map<string, MountingCalcResult>()
    for (const { manufacturer, result } of partnerAccessoryResults) {
      const existing = byManufacturer.get(manufacturer)
      if (existing) {
        existing.items.push(...result.items)
      } else {
        byManufacturer.set(manufacturer, { items: [...result.items] } as MountingCalcResult)
      }
    }
    byManufacturer.forEach((merged, manufacturer) => addItemsToSystem(system, manufacturer, merged))
  }
}

async function calculate(
  form: FormApi,
  system: StudioSystemType,
  autoCalcEnabled: boolean | undefined,
  context: BomGeneratorContext,
  calcGeneration: number
): Promise<void> {
  const nativeMountingSystems = getNativeMountingSystems()
  const mountingName = system?.mounting
  const selectedPartnerAccessories = system?.partner_accessories
  if (!mountingName && !selectedPartnerAccessories?.length) return

  if (mountingName) {
    try {
      saveInputs(form, system)
    } catch (error) {
      console.warn('Error setting default mounting options for system:', system.uuid, error)
    }
  }

  const roofInputs = getRoofInputs(system)

  // Create a deep copy of the part of custom_data that this process can affect
  const originalCustomData = form.getState().values?.custom_data || {}
  const mutableMcsData = cloneDeep(originalCustomData._mcs || {})

  clearExistingComponents(system, mountingName, autoCalcEnabled, selectedPartnerAccessories)

  if (
    mountingName &&
    autoCalcEnabled &&
    roofInputs.length === 0 &&
    (mountingName === 'solarNestRooftop' || mountingName === 'solarNestGroundMount') &&
    nativeMountingSystems[mountingName as NativeMountingID]
  ) {
    const emptyTarget = new nativeMountingSystems[mountingName as NativeMountingID].class({
      panelBlocks: [],
      roofTypeName: null,
    })
    getCompatibility(system, emptyTarget.getCompatibilityParameters(), context.isFeatureEnabled)
  }

  const mountingResults: MountingCalcResult[] = []
  const partnerAccessoryResults: { manufacturer: string; result: MountingCalcResult }[] = []

  const calculateRoof = async (roofInput) => {
    // calculate mounting system
    if (mountingName && autoCalcEnabled) {
      const target = new nativeMountingSystems[mountingName as NativeMountingID].class(roofInput)

      if (!context) {
        throw new Error('BomGeneratorContext is required for mounting calculations')
      }
      const compatibility = getCompatibility(system, target.getCompatibilityParameters(), context.isFeatureEnabled)
      if (!compatibility) return false

      const structuralOptions = await getStructuralOptions(target, system, mountingName)
      target.input.options.structuralOptions = structuralOptions

      const getInputOptions = () => {
        const integrationJson = window.editor.selectedSystem.integration_json
        if (!integrationJson) return {}

        if (integrationJson[mountingName]) {
          const options = pick(integrationJson[mountingName], possibleInputVariables)
          return options
        } else return {}
      }

      const inputOptions = getInputOptions()
      const { portrait, landscape } = getPanelOrientationsInSystem(system)
      if (portrait && landscape) {
        inputOptions.splitRoofHooksByOrientation = true
      }

      target.input.options = { ...target.input.options, ...inputOptions }

      const result: MountingCalcResult = await target.calculate(context)

      mountingResults.push(result)

      saveRoofFacetData(system, result, mutableMcsData)
    }

    // calculate partner accessories
    if (selectedPartnerAccessories?.length) {
      for (const partnerAccessory of selectedPartnerAccessories) {
        const target = new partnerAccessories[partnerAccessory as PartnerAccessoriesID].class(roofInput) as any

        const getInputOptions = () => {
          const integrationJson = window.editor.selectedSystem.integration_json
          if (!integrationJson) return {}

          let inputOptions = {}

          // get partner accessories inputs
          inputOptions = { ...integrationJson[partnerAccessory] }
          return inputOptions || {}
        }

        const inputOptions = getInputOptions()
        target.input.options = { ...target.input.options, ...inputOptions }

        const result: MountingCalcResult = await target.calculate(context)

        partnerAccessoryResults.push({ manufacturer: partnerAccessories[partnerAccessory].manufacturer, result })
      }
    }

    return true
  }

  const promises = roofInputs.map(calculateRoof)
  await Promise.all(promises)

  // Spare parts % and pack conversion must be applied once to the merged
  // system total (not per-roof-facet) to avoid inflated quantities from
  // cumulative Math.ceil rounding across separate facets.
  const mountingInputOptions = mountingName
    ? ((system?.integration_json?.[mountingName] ?? {}) as Record<string, unknown>)
    : {}

  if (!isMountingCalcGenerationCurrent(system.uuid, calcGeneration)) {
    return
  }

  addMergedItemsToSystem(
    system,
    mountingName as NativeMountingID,
    mountingResults,
    partnerAccessoryResults,
    mountingInputOptions
  )

  if (mountingName === 'solarPortGroundMount') {
    solarPortAfterCalculate(system, mountingResults)
  }

  if (mountingName === 'snapnrackUltraRail') {
    snapnrackUltraRailAfterCalculate(system)
  }

  const originalMcsDataCopy = cloneDeep(originalCustomData._mcs || {})
  const hasChanges = !isEqual(originalMcsDataCopy, mutableMcsData)

  if (hasChanges) {
    // Get the latest custom_data in case it was modified during the async operations
    const currentCustomData = cloneDeep(form.getState().values?.custom_data || {})

    const updatedCustomData = {
      ...currentCustomData,
      _mcs: mutableMcsData,
    }

    form.mutators.updateField('custom_data', updatedCustomData)
    if (form.mutators.markFieldAsDirty) {
      form.mutators.markFieldAsDirty('custom_data')
    }
  }
}

async function sync(
  form: FormApi,
  system: StudioSystemType,
  context: BomGeneratorContext,
  attributeName?: string
): Promise<void> {
  const calcGeneration = bumpMountingCalcGeneration(system.uuid)

  // Clear save-time MCS error only when user changes mounting system (not partner accessories)
  if (attributeName === 'mounting') {
    window.WorkspaceHelper.removeProjectErrorFromReduxStore(`PROJECT_SAVE_MCS`, system.uuid, 'plugin')
  }

  const nativeMountingSystems = getNativeMountingSystems()
  const mountingName = system?.mounting
  const selectedPartnerAccessories = system?.partner_accessories
  const nativeMountingSelected =
    typeof mountingName === 'string' && Object.keys(nativeMountingSystems).includes(mountingName)

  if (mountingName) {
    if (nativeMountingSelected) {
      initMountingData(system, mountingName)
    } else {
      clear(system)
    }
  } else {
    clear(system)
  }

  if (mountingName === 'solarPortGroundMount') {
    solarPortOnSync(system)
  }

  if (mountingName === 'nova') {
    novaOnSync(system)
  }
  if (mountingName === 'vanDerValk' && nativeMountingSelected) {
    refreshVanDerValkModuleGridFacetAttachments(system)
  }

  if (mountingName === 'fastRack') {
    fastRackOnSync(system)
  }

  const autoCalcEnabled =
    mountingName && nativeMountingSelected && system?.integration_json && system.integration_json[mountingName]?.enabled

  const skipVanDerValkBomUntilSuitability =
    mountingName === 'vanDerValk' &&
    nativeMountingSelected &&
    Boolean(autoCalcEnabled) &&
    !isVanDerValkSuitabilityReadyForBom(system)

  if ((autoCalcEnabled || selectedPartnerAccessories?.length) && !skipVanDerValkBomUntilSuitability) {
    await calculate(form, system, autoCalcEnabled, context, calcGeneration)
  } else if (skipVanDerValkBomUntilSuitability) {
    clear(system)
    const target = new nativeMountingSystems.vanDerValk.class({
      panelBlocks: [],
      roofTypeName: null,
    })
    getCompatibility(system, target.getCompatibilityParameters(), context.isFeatureEnabled)
    //@ts-ignore
    OsOther.refreshVisibility(system.others())
  } else {
    if (mountingName === 'vanDerValk' && nativeMountingSelected) {
      const target = new nativeMountingSystems.vanDerValk.class({
        panelBlocks: [],
        roofTypeName: null,
      })
      getCompatibility(system, target.getCompatibilityParameters(), context.isFeatureEnabled)
    }
    // Force System panel to refresh and exit
    //@ts-ignore
    OsOther.refreshVisibility(system.others())
  }
  return
}

export function clear(system: StudioSystemType): void {
  const nativeMountingSystems = getNativeMountingSystems()

  // Remove all mounting components from system
  const manufacturersToClear = Object.keys(nativeMountingSystems).map(
    (nativeMountingSystem) => nativeMountingSystems[nativeMountingSystem].manufacturer
  )
  manufacturersToClear.push('Genius Roof Solutions')
  manufacturersToClear.push('UNIRAC Solar')
  manufacturersToClear.push('Valk Solar System')
  const manufacturerSet = new Set(manufacturersToClear.map((manufacturer) => manufacturer.toLowerCase()))

  system
    .others()
    .filter((c) => {
      if (!isRackingManagedOther(c)) return false
      // Supplier-substituted SKUs keep the component brand as manufacturer_name.
      const mountingId = c.authored_by!.slice('racking:'.length)
      if (mountingId in nativeMountingSystems) return true
      return manufacturerSet.has((c.manufacturer_name ?? '').toLowerCase())
    })
    .forEach((c) => {
      if (c.getSystem && c.getSystem()?.uuid === system.uuid) {
        window.editor.removeObject(c)
      }
    })

  // Remove any warnings
  Object.keys(nativeMountingSystems).forEach((mountingSystem) => {
    window.WorkspaceHelper.removeProjectErrorFromReduxStore(
      `${mountingSystem.toUpperCase()}_PLUGIN_WARNING`,
      system.uuid,
      'plugin'
    )
    window.WorkspaceHelper.removeProjectErrorFromReduxStore(
      `${mountingSystem.toUpperCase()}_INPUT_WARNING`,
      system.uuid,
      'plugin'
    )
    if (mountingSystem === 'vanDerValk') {
      window.WorkspaceHelper.removeProjectErrorFromReduxStore('VANDERVALK_API_WARNING', system.uuid, 'plugin')
    }
    window.WorkspaceHelper.removeProjectErrorFromReduxStore(
      `${mountingSystem.toUpperCase()}_STRUCTURAL_RESULT`,
      system.uuid,
      'plugin'
    )
    window.WorkspaceHelper.removeProjectErrorFromReduxStore(
      `${mountingSystem.toUpperCase()}_STRUCTURAL_INFO`,
      system.uuid,
      'plugin'
    )
    window.WorkspaceHelper.removeProjectErrorFromReduxStore(
      `${mountingSystem.toUpperCase()}_FACING_INFO`,
      system.uuid,
      'plugin'
    )
    window.WorkspaceHelper.removeProjectErrorFromReduxStore(
      `${mountingSystem.toUpperCase()}_PANEL_SETUP_INFO`,
      system.uuid,
      'plugin'
    )
  })

  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`FASTRACK_RT_MINI_SPAN_WARNING`, system.uuid, 'plugin')
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`FASTRACK_SNOW_WARNING`, system.uuid, 'plugin')
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`FASTRACK_CLAMP_DEPTH_WARNING`, system.uuid, 'plugin')
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`FASTRACK_PREFERRED_RAILS_WARNING`, system.uuid, 'plugin')
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(
    `FASTRACK_HIDDEN_CLAMP_LANDSCAPE_WARNING`,
    system.uuid,
    'plugin'
  )
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`K2UK_THERMAL_SEPARATION_WARNING`, system.uuid, 'plugin')
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`K2UK_MULTIRAIL_ORIENTATION_WARNING`, system.uuid, 'plugin')
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`NOVA_TILE_INFO`, system.uuid, 'plugin')
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`NOVA_2400RAIL_INFO`, system.uuid, 'plugin')
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`NOVA_MIXED_ORIENTATION`, system.uuid, 'plugin')

  clearSnapnrackUltraRailPluginErrors(system.uuid)

  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`UNIRAC_ERROR`, system.uuid, 'mounting-integration')
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(
    `MOUNTING_ARCHIVED_COMPONENTS`,
    system.uuid,
    'mounting-integration'
  )
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`PROJECT_SAVE_MCS`, system.uuid, 'plugin')
}

function clearPartnerAccessories(system: StudioSystemType): void {
  // Remove all partner accessories from system
  Object.keys(partnerAccessories).forEach((partnerAccessoryId) => {
    removePartnerAccessory(system, partnerAccessoryId as PartnerAccessoriesID)
  })
}

export function removePartnerAccessory(system: StudioSystemType, partnerAccessoryId: PartnerAccessoriesID): void {
  const partnerAccessory = partnerAccessories[partnerAccessoryId]
  system
    .others()
    .filter((c) => c.manufacturer_name.toLowerCase() === partnerAccessory.manufacturer.toLowerCase())
    .filter((c) => isRackingManagedOther(c))
    .forEach((c) => {
      if (c.getSystem && c.getSystem()?.uuid === system.uuid) {
        window.editor.removeObject(c)
      }
    })
}

// function refreshAllSystems(): void {
//   window.editor.getSystems().forEach((system) => {
//     sync(system)
//   })
// }

export function refreshSystemFromObject(
  form: FormApi,
  object: any,
  context: BomGeneratorContext,
  attributeName?: string
) {
  const system = getSystemFromObject(object)
  if (system) {
    sync(form, system, context, attributeName)
  }
}

function getSystemFromObject(object: any): StudioSystemType | undefined {
  // If object is already an OsSystem, use it directly
  if (object.type === 'OsSystem') {
    return object as StudioSystemType
  }

  if (object.getSystem) {
    const system = object.getSystem()
    if (system) {
      return system
    }

    // Deleted objects don't have getSystem(), try parentBeforeRemoval
    if (object.parentBeforeRemoval?.getSystem) {
      return object.parentBeforeRemoval.getSystem()
    }
  }
}

// Deduplicate pending signals by system UUID so we only run one sync per system per debounce window
const pendingSignals = new Map<
  string,
  { form: FormApi; system: StudioSystemType; context: BomGeneratorContext; attributeName?: string }
>()

const ELECTRICAL_DEPENDENT_MOUNTING_SYSTEMS: ReadonlySet<string> = new Set(['nova', 'fastRack', 'snapnrackUltraRail'])
/** Roof facet signals — module grids on the facet plus selected system when mounting is Van der Valk. */
function getVanDerValkSystemsAffectedByFacetChange(facet: {
  moduleGrids?: () => Array<{ getSystem?: () => StudioSystemType | undefined }>
}): StudioSystemType[] {
  const byUuid = new Map<string, StudioSystemType>()

  for (const mg of facet.moduleGrids?.() ?? []) {
    const sys = mg.getSystem?.()
    if (sys?.mounting === 'vanDerValk') {
      byUuid.set(sys.uuid, sys)
    }
  }

  const selected = window.editor?.selectedSystem
  if (selected?.mounting === 'vanDerValk') {
    byUuid.set(selected.uuid, selected)
  }

  return [...byUuid.values()]
}

function queueMountingRefreshForSystems(
  form: FormApi,
  systems: StudioSystemType[],
  context: BomGeneratorContext,
  attributeName?: string
): void {
  if (!systems.length) {
    return
  }
  for (const system of systems) {
    pendingSignals.set(system.uuid, { form, system, context, attributeName })
  }
  processPendingSignalsDebounced()
}

export const refreshSystemFromObjectDebounced = (
  form: FormApi,
  object: any,
  context: BomGeneratorContext,
  attributeName?: string
) => {
  const objectType = object?.type
  const isModuleRelatedType = ['OsModuleGrid', 'OsModule'].includes(objectType)
  const isElectricalType =
    ['OsInverter', 'OsString', 'OsMppt'].includes(objectType) ||
    (objectType === 'OsOther' && object?.other_component_type === 'dc_optimizer')
  const mountingChangeAttributes = ['mounting', 'partner_accessories']
  const isMountingChange = attributeName && mountingChangeAttributes.includes(attributeName)

  if (isModuleRelatedType || isElectricalType || isMountingChange) {
    const system = getSystemFromObject(object)
    if (!system) return
    if (isElectricalType && (!system.mounting || !ELECTRICAL_DEPENDENT_MOUNTING_SYSTEMS.has(system.mounting))) return
    pendingSignals.set(system.uuid, { form, system, context, attributeName })
    processPendingSignalsDebounced()
  }

  const isFacetChange = objectType === 'OsFacet'
  const mountingRefreshAttributes = ['mounting', 'partner_accessories', 'facet']
  const isMountingRefresh = Boolean(attributeName && mountingRefreshAttributes.includes(attributeName))

  if (isFacetChange) {
    queueMountingRefreshForSystems(
      form,
      getVanDerValkSystemsAffectedByFacetChange(object),
      context,
      VDV_FACET_SURFACE_SYNC_ATTRIBUTE
    )
    return
  }

  if (isModuleRelatedType || isMountingRefresh) {
    const system = getSystemFromObject(object)
    if (!system) {
      return
    }
    queueMountingRefreshForSystems(form, [system], context, attributeName)
  }
}

const processPendingSignalsDebounced = debounce(
  function () {
    const snapshot = new Map(pendingSignals)
    pendingSignals.clear()
    snapshot.forEach(({ form, system, context, attributeName }) => {
      refreshSystemFromObject(form, system, context, attributeName)
    })
  },
  500,
  { leading: false, trailing: true }
)

export function syncMounting(form, system, context: BomGeneratorContext) {
  if (system.mounting) {
    saveInputs(form, system)
    refreshSystemFromObject(form, system, context)
  }
}
